import React, { useEffect, useState } from 'react';
import { Col } from 'antd';
import { Analytics } from './analytics/Analytics';
import { useTranslation } from 'react-i18next';
import './Profile.scss';
import MyLeaves from '../myLeaves/MyLeaves';
import { BREAKPOINT_BIG } from '../../consts';
import { useParams } from 'react-router';
import { UserStore } from '../../store/User';
import { observer } from 'mobx-react';
import { ReactComponent as PrevIcon } from '../../assets/icons/reports/prev-icon.svg';
import { useNavigate } from 'react-router-dom';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import DaysAvailable from '../daysAvailable/DaysAvailable';
import { TypePageDaysAvailable } from '../../types';
import { HOME_ROUTE } from '../../utils/consts';
import { useDeviceSizeThreshold } from '../../utils/hooks';
import { GlobalSearchStore } from '../../store/GlobalSearch';
import { Alert } from '@iba-team/portal-react-components/dist';

export interface ProfileComponentsParams{
  userId?: string,
  className?: string,
}

interface ProfileView {
  profile: 'LIMITED' | 'FULL',
  details: boolean,
  balance: boolean,
  analytics: boolean,
}

const defaultView: ProfileView = {
  profile: 'FULL',
  details: true,
  balance: true,
  analytics: true,
};

export const Profile = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {profile, fetchUserProfile, fetchAnotherUser, anotherUser, isFetchedAnotherUser} = UserStore;
  const [headerTitle, setHeaderTitle] = useState<{text: string, arrow: boolean}>({text:'', arrow: false});
  const isBigScreen = useDeviceSizeThreshold(BREAKPOINT_BIG);
  const {id} = useParams<string>();
  const { getLeaveTypesList } = LeaveTypesStore;
  const {checkRelations} = GlobalSearchStore;

  const [view, setView] = useState<ProfileView>({
    profile: 'LIMITED',
    details: false,
    balance: false,
    analytics: false,
  });

  useEffect(() => {
    if (profile.id === 0) {
      fetchUserProfile();
    }
  }, []);

  useEffect(() => {
    if(id) {
      if (+id !== profile.id) {
        checkRelations(+id).then(res => {
          setView({
            profile: res.includes('profile.employee.lr.full_info.view') ? 'FULL' : 'LIMITED',
            details: res.includes('profile.employee.lr.details.view'),
            balance: res.includes('profile.employee.balance.view'),
            analytics: res.includes('profile.employee.balance.analytics.view'),
          });
        });
      } else {
        setView(defaultView);
      }
    } else {
      setView(defaultView);
    }
  }, [id, profile]);

  const isExtendedView = view.profile === 'FULL';

  const columnsProfile: {title: string, dataIndex: string, key: string, width?: number, className: string}[] =
    isExtendedView ?  [
      { title: `${ t('attachment') }`, dataIndex: 'attachment', key: 'attachment', width: isBigScreen ? 120 : undefined,  className: 'my-leaves-table-cell'   },
      { title: `${ t('comment') }`, dataIndex: 'comment', key: 'comment', width: isBigScreen ? 120 : undefined ,  className: 'my-leaves-table-cell'  },
    ] : [];

  useEffect(()=>{
    if(id){
      fetchAnotherUser(id);
    }
    else if(profile.id !== 0){
      fetchAnotherUser(profile.id.toString());
    }
  },[id, profile]);

  useEffect(()=> {
    if(id){
      setHeaderTitle(profile.id.toString() === id ?
        {text: t('myProfile'), arrow: true}
        :
        {text: anotherUser.name, arrow: true} );
    }
    else{
      setHeaderTitle({text: t('myProfile'), arrow: true});
    }
  },[id, anotherUser]);

  useEffect(() => {
    getLeaveTypesList();
  },[]);


  return (
    <div className={`${!isFetchedAnotherUser && 'profile_blur_wrapper'}`}>
      <div className={'headerTitle'}>
        {headerTitle.arrow && <PrevIcon onClick={() => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          navigate(-1, { replace: true }) || navigate(HOME_ROUTE, { replace: true });
        }}
        style={{ cursor: 'pointer' }} />}
        <div className={'headerTitle_text'}>{headerTitle.text}</div>
      </div>
      {!anotherUser.isAllUserRequiredDataPresent && <Alert type='warning'>{t('messagesForBanners.noData.message')}</Alert> }
      <div className={'profile_stats'}>
        {view.balance && <Col span={12}>
          <DaysAvailable
            userId={id ? +id : anotherUser.id}
            className={anotherUser.isAllUserRequiredDataPresent ? 'days-available-profile' : 'days-available-profile_nodata'}
            activePage={TypePageDaysAvailable.PROFILE}
            isUserDataValid={anotherUser.isAllUserRequiredDataPresent} />
        </Col>}
        {view.analytics && <Col span={12}>
          <Analytics userId={id} className={!anotherUser.isAllUserRequiredDataPresent ? 'analytics-profile_nodata' : ''} />
        </Col>}
      </div>
      <MyLeaves userId={id} columnsProfile={columnsProfile} isEmployee={!view.details} isUserDataValid={anotherUser.isAllUserRequiredDataPresent}/>
    </div>);
});
